import { useMemo } from "react";

import { Autocomplete, Box, FormControl, Stack, TextField } from "@mui/material";

/** first_name, last_name, and business_name only when first+last are not both present; then token/email fallback. */
export function getBrokerDisplayLabel(option) {
  if (!option) return "";
  const first = String(option.first_name ?? "").trim();
  const last = String(option.last_name ?? "").trim();
  const business = String(option.business_name ?? "").trim();
  const hasFullPersonName = Boolean(first && last);
  const parts = [first, last, hasFullPersonName ? "" : business].filter(Boolean);
  if (parts.length > 0) return parts.join(", ");
  const token = String(option.agent_token ?? "").trim();
  if (token) return token;
  const email = String(option.email ?? "").trim();
  if (email) return email;
  return "Unnamed broker";
}

export function dedupeBrokersByToken(list) {
  if (!list?.length) return [];
  const seen = new Set();
  return list.filter((b) => {
    const k = String(b?.agent_token ?? b?.id ?? "").toLowerCase();
    if (!k) return true;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Unique lowercase agent tokens — keeps filter_agent_token / storage from growing duplicates. */
export function normalizeAgentTokens(tokens) {
  if (!tokens?.length) return [];
  const seen = new Set();
  const out = [];
  for (const t of tokens) {
    const k = String(t ?? "")
      .trim()
      .toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

export default function AgentAutoComplete({ brokers, onChange, selectedBroker, multiple = true }) {
  const options = useMemo(() => dedupeBrokersByToken(brokers || []), [brokers]);

  // options are deduped; value must be too or Autocomplete renders one chip per duplicate AgencyAgent row
  const value = useMemo(() => {
    if (multiple) {
      return dedupeBrokersByToken(Array.isArray(selectedBroker) ? selectedBroker : selectedBroker ? [selectedBroker] : []);
    }
    if (Array.isArray(selectedBroker)) return dedupeBrokersByToken(selectedBroker)[0] ?? null;
    return selectedBroker ?? null;
  }, [selectedBroker, multiple]);

  const handleChange = (event, newValue) => {
    if (multiple) {
      onChange(dedupeBrokersByToken(newValue || []));
    } else {
      onChange(newValue);
    }
  };

  return (
    <FormControl sx={{ width: "480px" }}>
      <Autocomplete
        multiple={multiple}
        options={options}
        getOptionLabel={getBrokerDisplayLabel}
        getOptionKey={(option) => option?.agent_token ?? option?.id ?? option?.email ?? getBrokerDisplayLabel(option)}
        isOptionEqualToValue={(option, value) =>
          (option?.agent_token || "").toLowerCase() === (value?.agent_token || "").toLowerCase()
        }
        value={value}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            name="filter_agent_token"
            {...params}
            label="Broker"
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{
              ...params.inputProps,
              "data-testid": "broker-dropdown",
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}
              >
                <Box>{getBrokerDisplayLabel(option)}</Box>
              </Stack>
            </li>
          );
        }}
      />
    </FormControl>
  );
}
